import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import paymentService from '@/services/http/api/payment'
import resource from '@/services/http/api/resource'

import { PAYMENT_TYPE } from '@/constants/payment'

import type { PaymentId, TODO } from '@/types'
import { useI18n } from 'vue-i18n'
import { AxiosError } from 'axios'

export const usePaymentStore = defineStore('payment', () => {
  const loader = ref<boolean>(false)
  const payment = ref<TODO>(null)
  const installments = ref<TODO>(null)
  const cancelPaymentModalVisibility = ref<boolean>(false)
  const editPaymentModalVisibility = ref<boolean>(false)
  type EditPaymentModal = {
    data: {
      amount: TODO
      status: TODO
    }
    resource: TODO
  }
  const editPaymentModal = ref<EditPaymentModal>({
    data: {
      amount: null,
      status: null
    },
    resource: null
  })
  const modalLoader = ref<boolean>(false)

  const errorMsg = ref<string>('')

  const getEditPaymentModalDataField = computed(() => editPaymentModal.value.data)
  const getEditPaymentModalResourceField = computed(() => editPaymentModal.value.resource)
  const isFundraising = computed(() => [PAYMENT_TYPE.fundraising].includes(payment.value?.type.id))
  const isDonate = computed(() => [PAYMENT_TYPE.fundraisingDonate].includes(payment.value?.type.id))
  const getPaymentStatus = computed(() => payment.value?.status)

  const setLoader = (value: boolean) => (loader.value = value)
  const setPayment = (value: TODO) => (payment.value = value)
  const setInstallments = (value: TODO) => (installments.value = value)
  const setCancelPaymentModalVisibility = (value: boolean) =>
    (cancelPaymentModalVisibility.value = value)
  const setEditPaymentModalVisibility = (value: boolean) =>
    (editPaymentModalVisibility.value = value)
  const setEditPaymentModalDataField = (field: TODO) =>
    (editPaymentModal.value.data = { ...editPaymentModal.value.data, ...field })
  const setEditPaymentModalResource = (value: TODO) => (editPaymentModal.value.resource = value)
  const setModalLoader = (value: boolean) => (modalLoader.value = value)
  const clear = () => {
    payment.value = null
    installments.value = null
    loader.value = false
    modalLoader.value = false
    cancelPaymentModalVisibility.value = false
    editPaymentModalVisibility.value = false
  }

  const getPayment = async (paymentId: TODO) => {
    if (!Number.isInteger(paymentId)) {
      class CustomError extends Error {
        response: { status: number }
        constructor(message: string, status: number) {
          super(message)
          this.response = { status }
        }
      }
      const error = new CustomError('invalid payment id', 404)
      return Promise.reject(error)
    }
    setLoader(true)

    try {
      const { data } = await paymentService.getPayment(paymentId)
      const { payment: paymentData = null, installments: installmentsData = null } = data.data
      setPayment(paymentData)
      setInstallments(installmentsData)
      return data.data
    } finally {
      setLoader(false)
    }
  }

  const cancelPayment = async (paymentId: TODO) => {
    setModalLoader(true)
    try {
      const { data } = await paymentService.cancelPayment(paymentId)
      setPayment(data.data)
      return data.data
    } finally {
      setModalLoader(false)
      setCancelPaymentModalVisibility(false)
    }
  }
  const getEditPaymentModalResource = async () => {
    const { data } = await resource.traditionalPaymentStatus()
    setEditPaymentModalResource(data.data)
  }

  const editPayment = async (payload: TODO) => {
    setModalLoader(true)
    try {
      const { data } = await paymentService.editPayment(payload)
      setPayment(data.data)
    } finally {
      setModalLoader(false)
      setEditPaymentModalVisibility(false)
    }
  }

  const editDonorData = async (payload: { data: TODO; paymentId: TODO }) => {
    setModalLoader(true)

    const entries = Object.entries(payload.data).filter(([, v]) => v != null)
    const data = Object.fromEntries(entries)

    if (typeof data.phoneNumber === 'string') {
      data.phoneNumber = data.phoneNumber.replace(/\s/g, '')
    }
    if (typeof data.postalCode === 'string') {
      data.postalCode = data.postalCode.replace('-', '')
    }

    try {
      const { data: response } = await paymentService.editDonorData(data, payload.paymentId)
      setPayment(response.data)
    } finally {
      setModalLoader(false)
    }
  }

  const createTraditional = async (paymentId: TODO) => {
    setModalLoader(true)
    try {
      return await paymentService.createTraditional(paymentId)
    } finally {
      setModalLoader(false)
    }
  }

  const editPaymentNote = async (payload: { paymentNote: TODO; paymentId: TODO }) => {
    setModalLoader(true)

    const data = {
      note: payload.paymentNote
    }

    try {
      const { data: response } = await paymentService.updatePaymentNote(data, payload.paymentId)
      setPayment(response.data)
    } finally {
      setModalLoader(false)
    }
  }

  const resendInstallmentNotification = async (paymentId: PaymentId) => {
    setModalLoader(true)

    try {
      const { data } = await paymentService.resendInstallmentNotification(paymentId)
      setPayment(data.data)
    } catch (error: TODO) {
      if (error instanceof AxiosError && error?.response?.status === 400) {
        return false
      } else {
        throw error
      }
    } finally {
      setModalLoader(false)
    }

    return true
  }

  const { t } = useI18n()

  const knownErrors = {
    child_edition_not_found: t('modal.editPaymentChild.errors.childEditionNotFound'),
    wrong_child_edition_status: t('modal.editPaymentChild.errors.wrongChildEditionStatus'),
    wrong_child_edition_edition: t('modal.editPaymentChild.errors.wrongChildEditionEdition'),
    child_edition_already_has_payments: t(
      'modal.editPaymentChild.errors.childEditionAlreadyHasPayments'
    ),
    child_edition_doesnt_have_fundraising: t(
      'modal.editPaymentChild.errors.childEditionDoesntHaveFundraising'
    ),
    not_latest_installments_payment: t('modal.editPaymentChild.errors.notLatestInstallmentsPayment')
  }

  function getErrorTranslation(errorCode: keyof typeof knownErrors) {
    return knownErrors[errorCode] ?? t('modal.editPaymentChild.errors.other')
  }

  const switchChildEdition = async (childEditionId: number, paymentId: string) => {
    setModalLoader(true)
    errorMsg.value = ''
    try {
      const response = await paymentService.switchChildEdition(childEditionId, paymentId)
      setPayment(response.data.data)
    } catch (error: TODO) {
      if (error instanceof AxiosError) {
        const code = error?.response?.data?.error
        errorMsg.value = getErrorTranslation(code)
      } else throw error
    } finally {
      setModalLoader(false)
    }
  }

  return {
    // state
    loader,
    payment,
    installments,
    cancelPaymentModalVisibility,
    editPaymentModalVisibility,
    editPaymentModal,
    modalLoader,
    errorMsg,
    // getters
    getEditPaymentModalDataField,
    getEditPaymentModalResourceField,
    isFundraising,
    isDonate,
    getPaymentStatus,
    // actions
    setLoader,
    setPayment,
    setInstallments,
    setCancelPaymentModalVisibility,
    setEditPaymentModalVisibility,
    setEditPaymentModalDataField,
    setEditPaymentModalResource,
    setModalLoader,
    clear,
    getPayment,
    cancelPayment,
    getEditPaymentModalResource,
    editPayment,
    editDonorData,
    createTraditional,
    editPaymentNote,
    resendInstallmentNotification,
    switchChildEdition
  }
})
